/**
 * "Przełóż" — the keyboard's and the menu's way of doing what a drag on the grid does: a day, a
 * start and a length, sent through the very same reschedule the drop sends
 * (`useSessionWrites.reschedule`). Not a reduced fallback: a length changed here is a resize.
 *
 * The fields are read by `model/sessionMove`'s `readMoveFields` — the drop's rules plus an empty
 * date or time — before anything is sent. A field in the way is marked and says why under itself,
 * and focus goes to it; the browser's own validation is off, so there is one voice, not two. Any
 * edit clears the server's last refusal. Opens with focus on its first field; Anuluj and a
 * successful move close it and hand focus back to the row that opened it.
 *
 * Under the fields stands the chosen day's picture (`DayOpenings`, calendar-management R9): what
 * holds it — this session excepted — and the free hours still ahead; picking an hour fills the
 * time field.
 */

import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import ember from '~/shared/ui/emberButton.module.css';
import { FormField } from '~/shared/ui/FormField';

import type { BookingSession } from '../model/bookingSession';
import type { TimeSpan } from '../model/busyTime';
import { DURATION_BOUNDS, STEP_MINUTES, isBookableLength } from '../model/sessionDraft';
import { readMoveFields, type MoveField, type MoveFields } from '../model/sessionMove';
import { minutesOfTime, mondayOfDay, paddedClock, wallReading } from '../model/trainerClock';
import compose from './ComposeForm.module.css';
import { DayOpenings } from './DayOpenings';
import styles from './SidePanel.module.css';

const PROBLEM_KEYS = {
  noDate: 'web:booking.move.problem.noDate',
  noTime: 'web:booking.move.problem.noTime',
  duration: 'web:booking.create.problem.duration',
  midnight: 'web:booking.refusal.midnight_is_not_an_hour',
  past: 'web:booking.move.past',
} as const;

interface RescheduleFormProps {
  readonly session: BookingSession;
  readonly zone: string;
  readonly nowMs: number;
  readonly onReschedule: (target: TimeSpan) => Promise<boolean>;
  /** Any edit — the server's last refusal no longer describes what is in the fields. */
  readonly onEdit: () => void;
  /** Closed: `true` when the session was moved out of its week, `false` otherwise (Anuluj too). */
  readonly onDone: (movedToAnotherWeek: boolean) => void;
}

export function RescheduleForm({
  session,
  zone,
  nowMs,
  onReschedule,
  onEdit,
  onDone,
}: RescheduleFormProps) {
  const { t } = useTranslation();
  const box = useRef<HTMLFormElement>(null);
  const start = wallReading(session.startMs ?? 0, zone);
  const [fields, setFields] = useState<MoveFields>({
    day: start.day,
    time: paddedClock(start.minutes),
    length: String(session.durationMinutes),
  });
  const [shown, setShown] = useState<{ field: MoveField; message: string } | null>(null);
  const [saving, setSaving] = useState(false);
  // The day it stood on when the form opened — the session prop follows the refetch after a move.
  const [fromDay] = useState(start.day);

  useEffect(() => {
    box.current?.querySelector('input')?.focus();
  }, []);

  const edit = (patch: Partial<MoveFields>) => {
    setShown(null);
    onEdit();
    setFields((current) => ({ ...current, ...patch }));
  };
  const pickedMinutes = minutesOfTime(fields.time);
  const errorOf = (field: MoveField) => (shown?.field === field ? shown.message : null);

  return (
    <form
      ref={box}
      className={styles.moveForm}
      aria-label={t('web:booking.move.title')}
      aria-busy={saving}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (saving) return;
        const reading = readMoveFields(session, fields, zone, nowMs);
        if (!reading.ok) {
          // Committed first, so the field is focused already marked and described by its reason.
          flushSync(() => {
            setShown({ field: reading.field, message: t(PROBLEM_KEYS[reading.problem]) });
          });
          box.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
          return;
        }
        setSaving(true);
        void onReschedule(reading.target).then((moved) => {
          setSaving(false);
          if (moved) {
            onDone(
              mondayOfDay(wallReading(reading.target.startMs, zone).day) !== mondayOfDay(fromDay),
            );
          }
        });
      }}
    >
      <div className={compose.pair}>
        <FormField
          label={t('web:booking.create.date')}
          type="date"
          value={fields.day}
          error={errorOf('day')}
          onChange={(day) => {
            edit({ day });
          }}
        />
        <FormField
          label={t('web:booking.create.time')}
          type="time"
          step={STEP_MINUTES * 60}
          value={fields.time}
          error={errorOf('time')}
          onChange={(time) => {
            edit({ time });
          }}
        />
      </div>
      <FormField
        label={t('calendar.create.durationLabel')}
        type="number"
        inputMode="numeric"
        step={5}
        min={String(DURATION_BOUNDS.min)}
        max={String(DURATION_BOUNDS.max)}
        value={fields.length}
        error={errorOf('length')}
        onChange={(length) => {
          edit({ length });
        }}
        trailing={<span className={compose.unit}>{t('web:booking.create.minutes')}</span>}
      />
      {/^\d{4}-\d{2}-\d{2}$/.test(fields.day) ? (
        <DayOpenings
          day={fields.day}
          zone={zone}
          nowMs={nowMs}
          durationMinutes={
            isBookableLength(Number(fields.length))
              ? Number(fields.length)
              : session.durationMinutes
          }
          exceptId={session.id}
          candidate={pickedMinutes === null ? null : { minutes: pickedMinutes }}
          onPick={(minutes) => {
            edit({ time: paddedClock(minutes) });
          }}
        />
      ) : null}
      <div className={compose.formFoot}>
        <button
          type="button"
          className={compose.repeat}
          onClick={() => {
            onDone(false);
          }}
        >
          {t('web:booking.move.cancel')}
        </button>
        <span className={compose.spacer} />
        <button
          type="submit"
          className={`${String(ember.ember)} ${String(compose.submit)}`}
          aria-disabled={saving ? 'true' : undefined}
        >
          {saving ? t('web:booking.move.saving') : t('web:booking.move.submit')}
        </button>
      </div>
    </form>
  );
}
